import { API } from '@api/api'
import { queryClient } from '@api/queryClient'
import { queryKeys } from '@api/queryKeys'
import type {
  LoadPlanningControllerUpdateLoadPlanningData,
  PlannedStrategyControllerCreatePlannedStrategyData,
  PlannedStrategyControllerDeletePlannedStrategyData,
} from '@api/types/procurement'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { invalidateStrategyOverviewsQueries } from '../../overview/energy/hooks/useStrategyOverviews.hook'

interface FetchPlannedStrategyRequest {
  companyId?: number
}

export const invalidatePlannedStrategiesQueries = async () =>
  await queryClient.invalidateQueries({
    queryKey: queryKeys.procurement.plannedStrategies.fetchForCompany.toKey(),
  })

export const invalidateSpecificPlannedStrategy = async (strategyId?: string) =>
  await queryClient.invalidateQueries({
    queryKey: queryKeys.procurement.plannedStrategies.fetchOne.toKeyWithArgs(strategyId),
  })

const invalidatePlannedStrategyListQueries = () =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.plannedStrategies.toKey(),
    }),
    invalidateStrategyOverviewsQueries(),
  ])

const invalidatePlannedStrategyMutationQueries = () =>
  Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.loadAdjustments.toKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.loadAdjustmentsV2.toKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.loadPlanning.toKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.loadPlanningsV2.toKey(),
    }),
    invalidatePlannedStrategyListQueries(),
  ])

const invalidatePlannedStrategyUpdateMutationQueries = () =>
  Promise.all([
    invalidatePlannedStrategyMutationQueries(),
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.portfolio.toKey(),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.procurement.portfolioTimeseries.toKey(),
    }),
  ])

export const useFetchPlannedStrategies = (data: FetchPlannedStrategyRequest & { forecastEnabled: boolean }) =>
  useQuery({
    queryKey: queryKeys.procurement.plannedStrategies.fetchForCompany.toKeyWithArgs(data.companyId),
    queryFn:
      data.companyId && data.forecastEnabled ? () => API.procurements.fetchPlannedStrategies({ companyId: data.companyId! }) : skipToken,
  })

export const useGetPlannedStrategy = (data: { strategyId?: string; shouldRetrieveStrategy: boolean }) =>
  useQuery({
    queryKey: queryKeys.procurement.plannedStrategies.fetchOne.toKeyWithArgs(data.strategyId),
    queryFn:
      data.strategyId && data.shouldRetrieveStrategy
        ? () => API.procurements.fetchPlannedStrategy({ strategyId: data.strategyId! })
        : skipToken,
    enabled: !!data.strategyId,
  })

export const useCreatePlannedStrategy = () =>
  useMutation({
    mutationFn: (data: PlannedStrategyControllerCreatePlannedStrategyData) => API.procurements.createPlannedStrategy(data),
    onSuccess: invalidatePlannedStrategyMutationQueries,
  })

export const useDeletePlannedStrategy = (loadPlanningId?: string) =>
  useMutation({
    mutationFn: (data: PlannedStrategyControllerDeletePlannedStrategyData) => API.procurements.deletePlannedStrategy(data),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.procurement.loadPlanningsV2.toKey() })
      queryClient.removeQueries({ queryKey: queryKeys.procurement.loadPlanning.toKey() })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadAdjustments.toKey() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.procurement.loadAdjustmentsV2.toKey() }),
        invalidatePlannedStrategyListQueries(),
        invalidateSpecificPlannedStrategy(loadPlanningId),
      ])
    },
  })

export const useUpdatePlannedStrategy = () =>
  useMutation({
    mutationFn: (data: LoadPlanningControllerUpdateLoadPlanningData) => API.procurements.updateLoadForecast(data),
    onSuccess: invalidatePlannedStrategyUpdateMutationQueries,
  })
